import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { userDetailQueryOptions } from '../api/users.api';
import { useUpdateUser } from './useUpdateUser';
import {
  CLEARABLE_LOOKUP_FIELDS,
  CLEARABLE_TEXT_FIELDS,
  DATE_USER_FIELDS,
} from '../constants/user.constants';
import {
  updateUserSchema,
  type UpdateUserPayload,
  type UpdateUserRequest,
} from '../types/user.types';

const CLEARABLE_FIELD_SET: ReadonlySet<string> = new Set([
  ...CLEARABLE_LOOKUP_FIELDS,
  ...CLEARABLE_TEXT_FIELDS,
  ...DATE_USER_FIELDS,
]);

function nullIfCleared(
  field: keyof UpdateUserPayload,
  value: UpdateUserPayload[keyof UpdateUserPayload],
) {
  const isEmptyValue = value === undefined || value === '';
  const isClearedField = isEmptyValue && CLEARABLE_FIELD_SET.has(field);
  return isClearedField ? null : value;
}

interface UseEditUserFormArgs {
  userId: number;
  onLeave: () => void;
}

export function useEditUserForm({ userId, onLeave }: UseEditUserFormArgs) {
  const { mutate, isPending } = useUpdateUser(onLeave);

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isFetching: isUserDataFetching,
    error: userDataError,
    refetch: refetchUserData,
  } = useQuery(userDetailQueryOptions(String(userId)));

  const form = useForm<UpdateUserPayload>({
    resolver: zodResolver(updateUserSchema),
    mode: 'onTouched',
    defaultValues: {},
  });

  const {
    reset,
    watch,
    setValue,
    formState: { dirtyFields, isDirty },
  } = form;

  const prevCompanyIdRef = useRef<number | undefined>(undefined);
  const watchedCompanyId = watch('companyId');

  useEffect(() => {
    if (!userData) return;
    reset({
      fullName: userData.fullName,
      username: userData.username,
      hrEmpId: userData.hrEmpId,
      mobile: userData.mobile,
      email: userData.email ?? '',
      status: userData.status,
      companyId: userData.companyId ?? undefined,
      departmentId: userData.departmentId ?? undefined,
      designationId: userData.designationId ?? undefined,
      geoUnitId: userData.thanaId ?? undefined,
      parentUserId: userData.parentUserId ?? undefined,
      accountNumber: userData.accountNumber ?? '',
      nickname: userData.nickname ?? '',
      contactNo: userData.contactNo ?? '',
      address: userData.address ?? '',
      activeFrom: userData.activeFrom ?? undefined,
      activeTo: userData.activeTo ?? undefined,
    });
  }, [userData, reset]);

  useEffect(() => {
    const prev = prevCompanyIdRef.current;
    prevCompanyIdRef.current = watchedCompanyId;
    if (prev === undefined || prev === watchedCompanyId) return;
    setValue('departmentId', undefined);
  }, [watchedCompanyId, setValue]);

  function submit(values: UpdateUserPayload) {
    const changedFields = Object.keys(dirtyFields) as (keyof UpdateUserPayload)[];
    if (changedFields.length === 0) {
      onLeave();
      return;
    }
    const payload: UpdateUserRequest = Object.fromEntries(
      changedFields.map((field) => [field, nullIfCleared(field, values[field])]),
    );
    mutate({ id: String(userId), payload });
  }

  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty);

  return {
    form,
    userData,
    isUserDataLoading,
    isUserDataFetching,
    userDataError,
    refetchUserData,
    isPending,
    submit,
    askBeforeClose,
    discardChangesDialog,
  };
}
